// Added by Sejune Oh on 2019-06-04
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Suspense, Component, lazy } from 'react'
import { Switch, Route, Redirect, RouteComponentProps } from 'react-router'
import { inject, observer } from 'mobx-react'
import { InjectedIntlProps, injectIntl } from 'react-intl'
import { computed, observable, action, makeObservable } from 'mobx'

import { routerPath } from './helper/define'
import AuthRouter from './helper/AuthRouter'
import Intl from './helper/Intl'
import Logger from './helper/Logger'
import RouteLeavingGuard from './helper/RouteLeavingGuard'
import { UserAuthStore } from './stores/user/UserAuth'
import { PackageConfigStore } from './stores/mgmt/config'
import ProductStore from './stores/ProductStore'
import CLAppStore from './stores/CLAppStore'

// Loading gif
import { LoadingWrapper, LoadingImg } from './RootApp'

// Pages
const Login = lazy(() => import('./pages/login'))
const AppIcons = lazy(() => import('./pages/app-icons'))
const NetworkStatus = lazy(() => import('./pages/network-status'))
const App = lazy(() => import('./pages/app'))
const Setting = lazy(() => import('./pages/setting'))

// Interface
interface RootRouterCProps extends InjectedIntlProps, RouteComponentProps {
  userAuth?: UserAuthStore
  packageConfig?: PackageConfigStore
  clApp?: CLAppStore
  product?: ProductStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'packageConfig', 'clApp', 'product')
@observer
class RootRouter extends Component<RootRouterCProps> {
  _isErrorOccured: boolean = false
  _unsubscribeFromHistory: any
  _productKey: string = this.props.product!.productKey

  constructor(props: RootRouterCProps) {
    super(props)
    makeObservable(this, {
      _isErrorOccured: observable,
      _unsubscribeFromHistory: observable,
      componentDidCatch: action,
      userAuth: computed,
      packageConfig: computed,
    })
  }

  componentDidMount() {
    const { history, intl } = this.props
    Intl.bindIntl(intl)
    this._unsubscribeFromHistory = history.listen(this.handleLocationChange)
  }

  componentWillUnmount() {
    if (this._unsubscribeFromHistory) this._unsubscribeFromHistory()
  }

  componentDidCatch = (err: any) => {
    Logger.errLog('Error from mounting Root Router component')
    console.log(err)
    this._isErrorOccured = true
  }

  render() {
    if (this._isErrorOccured) return null

    const to =
      this.packageConfig!.checkDashboardEnable() && this.packageConfig!.checkDashboardIsLanding()
        ? // eslint-disable-next-line
          `/${routerPath.DASHBOARD}${location.search}`
        : // eslint-disable-next-line
          `/${routerPath.WORKLIST}${location.search}`

    return (
      <>
        <RouteLeavingGuard
          title={this._productKey.toUpperCase()}
          when={this.props.clApp!.isPromptRequired}
          navigate={(location: string) => this.props.history.push(location)}
          shouldBlockNavigation={() => this.props.clApp!.isPromptRequired}
          content={this.props.clApp!.promptMessage}
        />
        <Suspense
          fallback={
            <LoadingWrapper>
              <LoadingImg style={{ backgroundImage: this.props.product!.loadingGif }} />
            </LoadingWrapper>
          }
        >
          <Switch>
            <Route path={`/${routerPath.LOGIN}`} render={(routeProps) => <Login {...routeProps} />} />
            <Route path={`/${routerPath.APP_ICONS}`} render={() => <AppIcons />} />
            <Route path={`/${routerPath.NETWORK}`} render={() => <NetworkStatus />} />

            {/* Application Page (Viewer) */}
            <AuthRouter
              path={`/${routerPath.WORKLIST}`}
              redirectPath={`/${routerPath.LOGIN}`}
              isAuthenticated={this.userAuth.isSignedUp}
              component={App}
            />
            {/* Setting Page */}
            <AuthRouter
              path={`(/${routerPath.MGMT}|/${routerPath.DASHBOARD}|/${routerPath.WORKLIST})/`}
              redirectPath={`/${routerPath.LOGIN}`}
              isAuthenticated={this.userAuth.isSignedUp}
              component={Setting}
            />
            <Redirect from={`/${routerPath.MAIN}`} to={to} />
          </Switch>
        </Suspense>
      </>
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get userAuth() {
    return this.props.userAuth!
  }

  get packageConfig() {
    return this.props.packageConfig!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  handleLocationChange = () => {
    this.props.clApp!.clearMessageBeforeWebPageUnload()
  }
}

export default injectIntl(RootRouter)
